import {
  AlarmClock,
  ArrowRight,
  ArrowRightCircle,
  Award,
  Bell,
  CalendarClock,
  Check,
  CheckCircle2,
  ClipboardCheck,
  ClipboardList,
  Hourglass,
  MapPin,
  Newspaper,
  Siren,
  Sunrise,
  Trash2,
  XCircle,
  type LucideIcon,
} from 'lucide-react'
import { Button } from '@/components/ui'
import { useI18n } from '@/i18n/I18nProvider'
import type { TranslationKey } from '@/i18n/dictionary'
import { metaParts, type AppNotification, type DayGroup } from '@/lib/notifications'
import { INTL_LOCALE, cn, formatDateTime, formatRelative } from '@/lib/utils'
import { today } from '@/lib/timezone'

/*
 * The icon name comes from the server so the meaning of a notification lives
 * in one place. This map is only the name-to-component lookup.
 */
const ICONS: Record<string, LucideIcon> = {
  'clipboard-list': ClipboardList,
  'check-circle-2': CheckCircle2,
  'x-circle': XCircle,
  'arrow-right-circle': ArrowRightCircle,
  award: Award,
  hourglass: Hourglass,
  siren: Siren,
  // Reminders. These were once missing, so the scheduled digests silently fell
  // back to a plain bell and every notification looked identical in the list.
  // An unknown name degrades quietly rather than failing, which is right at
  // runtime and exactly why the gap survived — so when adding a notification,
  // add its icon here too.
  'alarm-clock': AlarmClock,
  'clipboard-check': ClipboardCheck,
  // The daily summary, under whichever of these names the server settles on.
  sunrise: Sunrise,
  newspaper: Newspaper,
  'calendar-clock': CalendarClock,
  bell: Bell,
}

/**
 * The icon by type, for when the server's icon name is unknown or absent.
 *
 * Second, not first: the server picks per row — an accepted improvement gets
 * an award, a rejected one a cross — and the type alone cannot say which.
 */
const TYPE_ICONS: Record<string, LucideIcon> = {
  urgentSubmission: Siren,
  submissionStatus: ArrowRightCircle,
  improvementReviewed: Award,
  taskAssigned: ClipboardList,
  overdueDigest: AlarmClock,
  missingEvaluations: ClipboardCheck,
  dailySummary: Sunrise,
}

function iconFor(notification: AppNotification): LucideIcon {
  return (
    (notification.icon && ICONS[notification.icon]) ||
    (notification.type && TYPE_ICONS[notification.type]) ||
    Bell
  )
}

/**
 * One notification: what happened, where, when, and what to do about it.
 *
 * The row itself is not a button any more. It used to be, and the only thing
 * it could do was open — so marking something read without leaving the page
 * meant opening it and coming back. The action is a button of its own now,
 * worded by the server ("Otvori prijavu", "Pogledaj zadatak"), so the row says
 * what pressing it will do rather than leaving it to be guessed.
 */
export function NotificationItem({
  notification,
  onAction,
  onMarkRead,
  onDelete,
  compact = false,
}: {
  notification: AppNotification
  /** Opens `action.path`; marking read is the caller's half of it. */
  onAction: (notification: AppNotification) => void
  onMarkRead: (notification: AppNotification) => void
  /** Only on the notification centre — the bell is for skimming, not tidying. */
  onDelete?: (notification: AppNotification) => void
  /**
   * The bell's narrow panel: "mark as read" becomes an icon beside the action
   * instead of a second line under it. Still named for a screen reader and on
   * hover — it is the same button, only without its words.
   */
  compact?: boolean
}) {
  const { t } = useI18n()
  const Icon = iconFor(notification)
  const unread = !notification.isRead
  const high = notification.importance === 'high'
  const meta = metaParts(notification)
  const hasPlace = Boolean(notification.where.workCenter || notification.where.plant)

  return (
    <li className="flex items-start gap-3 rounded-xl p-3 pointer-fine:py-2.5">
      {/* The icon gets a chip so unread rows are legible at a glance without
          tinting the whole row, which made the text harder to read than the
          ones already seen. Red for an unread urgent one — the one colour on
          the list that means "now". */}
      <span
        className={cn(
          'flex size-9 shrink-0 items-center justify-center rounded-lg',
          !unread
            ? 'bg-muted text-muted-foreground'
            : high
              ? 'bg-red-100 text-red-700 dark:bg-red-950/60 dark:text-red-300'
              : 'bg-brand-100 text-brand-700 dark:bg-brand-900/50 dark:text-brand-300',
        )}
      >
        <Icon className="size-4.5" aria-hidden />
      </span>

      <div className="min-w-0 flex-1">
        <div className="flex items-start gap-2">
          <p className={cn('min-w-0 flex-1 text-sm', unread ? 'font-semibold' : 'font-medium')}>
            {high && (
              // A word as well as the colour, for whoever cannot tell the red
              // chip from the blue one.
              <span
                className={cn(
                  'mr-1.5 inline-block rounded px-1.5 py-px align-[1px] text-[10px] font-semibold tracking-wide uppercase',
                  unread
                    ? 'bg-red-600 text-white'
                    : 'bg-muted text-muted-foreground',
                )}
              >
                {t('notifications.importance.high')}
              </span>
            )}
            {notification.title}
          </p>

          {/* A dot, not a "Novo" chip — it carries the same meaning in a tenth
              of the width, which matters on a row that already has three lines
              of text. */}
          {unread && (
            <span
              className="bg-brand-600 mt-1.5 size-2 shrink-0 rounded-full"
              role="img"
              aria-label={t('notifications.unreadMarker')}
            />
          )}
        </div>

        {notification.body && (
          <p className="muted mt-0.5 line-clamp-3 text-xs">{notification.body}</p>
        )}

        {/* Where, then when — on one line, because both are what you read to
            decide whether this one is yours to deal with. */}
        <p className="muted mt-1 flex flex-wrap items-center gap-x-1 text-xs">
          {hasPlace && <MapPin className="size-3 shrink-0" aria-hidden />}
          {meta.length > 0 && <span>{meta.join(' · ')} ·</span>}
          <time dateTime={notification.at ?? undefined} title={formatDateTime(notification.at)}>
            {formatRelative(notification.at)}
          </time>
        </p>

        {(notification.action || unread || onDelete) && (
          <div className="mt-2 flex flex-wrap items-center gap-1.5">
            {notification.action && (
              <Button variant="secondary" onClick={() => onAction(notification)}>
                {notification.action.label ?? t('notifications.open')}
                <ArrowRight className="size-4" aria-hidden />
              </Button>
            )}

            {unread && (
              <Button
                variant="ghost"
                onClick={() => onMarkRead(notification)}
                aria-label={compact ? t('notifications.markRead') : undefined}
                title={compact ? t('notifications.markRead') : undefined}
                className={cn(compact && 'px-3')}
              >
                <Check className="size-4" aria-hidden />
                {!compact && t('notifications.markRead')}
              </Button>
            )}

            {onDelete && (
              <Button
                variant="ghost"
                onClick={() => onDelete(notification)}
                aria-label={t('notifications.delete')}
                title={t('notifications.delete')}
                className="text-muted-foreground hover:text-destructive ml-auto px-3"
              >
                <Trash2 className="size-4" aria-hidden />
              </Button>
            )}
          </div>
        )}
      </div>
    </li>
  )
}

const KIND_HEADINGS: Record<Exclude<DayGroup<unknown>['kind'], 'date'>, TranslationKey> = {
  today: 'notifications.today',
  yesterday: 'notifications.yesterday',
  earlier: 'notifications.earlier',
}

/**
 * "Danas", "Juče", or the date itself — with the weekday, because "utorak"
 * is how people remember last week, not "3. 11."
 *
 * The date is a calendar date already in the company's zone (see
 * `groupByDay`), so it is printed at noon UTC with the zone pinned to UTC:
 * formatting it in any real zone could move it across midnight.
 */
export function NotificationDayHeading({
  group,
  className,
  id,
}: {
  group: Pick<DayGroup<unknown>, 'kind' | 'date'>
  className?: string
  id?: string
}) {
  const { t, locale } = useI18n()

  let text: string

  if (group.kind === 'date' && group.date) {
    const sameYear = group.date.slice(0, 4) === today().slice(0, 4)

    text = new Date(`${group.date}T12:00:00Z`).toLocaleDateString(INTL_LOCALE[locale], {
      weekday: 'long',
      day: 'numeric',
      month: 'long',
      year: sameYear ? undefined : 'numeric',
      timeZone: 'UTC',
    })
  } else {
    text = t(KIND_HEADINGS[group.kind === 'date' ? 'earlier' : group.kind])
  }

  return (
    <h3
      id={id}
      className={cn('muted px-3 py-1.5 text-xs font-semibold tracking-wide uppercase', className)}
    >
      {text}
    </h3>
  )
}
